// POC-858: type-only import. The real SDK used to be statically bundled into
// main-*.js (every buyer downloaded it before /checkout could paint) even
// though sales init is deferred to idle. Runtime access goes through
// loadSentry() → separate async chunk, loaded on demand after first paint.
import type * as Sentry from "@sentry/react";
import { toast } from "react-hot-toast";
import packageJson from "../../package.json";
import { isSalesSurface } from "../lib/surface";
import {
	isAxiosNetworkError,
	isAxiosNetworkEventText,
	NETWORK_ERROR_TOAST,
	networkErrorToast,
} from "../utils/isAxiosNetworkError";

/**
 * Frontend Sentry wiring.
 *
 * Client-side failures (a toast.error shown to a buyer, a console.error in
 * checkout) report here. This module:
 *   1. Initialises the Sentry browser SDK.
 *   2. Records Session Replay so an issue carries the video of what the user
 *      actually did (see sentry-replay.ts for privacy).
 *   3. Bridges every `toast.error` / `toast.promise` rejection into Sentry.
 *   4. Exposes a reporter for `console.error` (wired in filterExtensionErrors).
 *
 * Sales loads this after first paint (`scheduleSalesSentry`) so rrweb stays
 * out of the checkout critical path (POC-657).
 */

const DEFAULT_DSN =
	"https://47918292afc0be0ea9d51cec43fcc3bb@o932815.ingest.us.sentry.io/4511582452973568";
const EXPLICIT_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const DSN = EXPLICIT_DSN || DEFAULT_DSN;
const ENV =
	(import.meta.env.VITE_APP_ENV as string | undefined) ??
	(import.meta.env.MODE as string | undefined) ??
	"development"; // pragma: allowlist secret
const isProd = ENV === "production";
const enabled =
	(ENV !== "development" && ENV !== "test" && ENV !== "offline") || // pragma: allowlist secret
	Boolean(EXPLICIT_DSN);

let initialised = false;
let scheduled = false;

type SentryModule = typeof import("@sentry/react");
let sentryModulePromise: Promise<SentryModule> | null = null;

const loadSentry = (): Promise<SentryModule> => {
	if (!sentryModulePromise) {
		sentryModulePromise = import("@sentry/react");
	}
	return sentryModulePromise;
};

const parseRate = (value: unknown, fallback: number): number => {
	const rate = Number.parseFloat(String(value ?? ""));
	return Number.isFinite(rate) && rate >= 0 && rate <= 1 ? rate : fallback;
};
const REPLAY_SESSION_SAMPLE_RATE = parseRate(
	import.meta.env.VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE,
	isProd ? 0.1 : 1.0,
);
const REPLAY_ERROR_SAMPLE_RATE = 1.0;

const startSessionReplay = () => {
	void import("./sentry-replay")
		.then(({ addReplayIntegration }) => addReplayIntegration())
		.catch(() => {});
};

const toMessage = (value: unknown): string => {
	if (typeof value === "string") return value;
	if (value instanceof Error) return value.message;
	return "Application error";
};

const isHandledClientError = (value: unknown): boolean => {
	const status =
		(value as { response?: { status?: unknown }; status?: unknown })?.response
			?.status ?? (value as { status?: unknown })?.status;
	return typeof status === "number" && status >= 400 && status < 500;
};

const isHandledClientErrorDetails = (value: unknown): boolean => {
	if (!value || typeof value !== "object") return false;
	const status = (value as { status?: unknown }).status;
	return typeof status === "number" && status >= 400 && status < 500;
};

const isValidationConsoleNoise = (args: unknown[]): boolean =>
	args.some(
		(arg) => typeof arg === "string" && arg.startsWith("Validation errors:"),
	);

const isFetchFileConsoleNoise = (args: unknown[]): boolean => {
	for (const arg of args) {
		const url = (arg as { config?: { url?: string } })?.config?.url;
		if (typeof url === "string" && url.includes("fetch-file")) return true;
		if (
			typeof arg === "string" &&
			(/fetch-file/.test(arg) || /Failed to download file from/.test(arg))
		) {
			return true;
		}
	}
	return false;
};

const isExtensionOrInAppBrowserNoise = (event: Sentry.ErrorEvent): boolean => {
	const frames =
		event.exception?.values?.flatMap((v) => v.stacktrace?.frames ?? []) ?? [];
	const noisyFrame = frames.some((f) => {
		const file = f.filename ?? "";
		const fn = f.function ?? "";
		return (
			/(chrome|moz|safari)-extension:\/\//.test(file) ||
			/iabjs:\/\/|navigation_performance_logger/.test(file) ||
			/^(sendDataToNative|sendPageHideMessage|sendJsBlockingTimeMessage)$/.test(
				fn,
			) ||
			// X/Twitter's in-app browser injects a footer script into the page
			// (`Can't find variable: CONFIG`, FRONTEND-83) — never our code.
			/^(updateFooterPositions|updateGapFiller)$/.test(fn)
		);
	});
	if (noisyFrame) return true;

	const message = event.exception?.values?.map((v) => v.value ?? "").join(" ");
	return /Java object is gone|window\.webkit\.messageHandlers/.test(
		message ?? "",
	);
};

const CROSS_ORIGIN_FRAME_RE =
	/Blocked a frame with origin .* from accessing a cross-origin frame/;

/**
 * Expected buyer/creator validation toasts. They must still show in the UI;
 * they must not page Sentry (POC-808 / leftover from POC-784 fingerprint-only).
 */
const EXPECTED_TOAST_RE =
	/Pay want price must be greater than|The plan must be at least|Failed to create dynamic plan: The plan must be at least|Purchases over \$2500|Subdomain does not match|Test mode is enabled|payouts account connected|Please complete:|Please complete all required fields|Invalid token|That link is invalid or expired|Error while verifying email|This store isn't ready to accept payments yet|Couldn't start checkout|Checkout couldn't start|Error while creating payment intent|Error while creating subscription payment intent|Network error\. Check your connection|Invalid value for parameter ['"]?plan\.|Failed to update brand color|Product not found|Subscription not found|Failed to save product|business\/store display name|Add your business address in Settings|couldn't save (tax|checkout) settings|^Delete Variant$|over your plan's .*upload limit|A record with the value .*already exists|Could not convert leftover balances|Error while creating bought products|Failed to issue refund|^User not found$|Failed to create checkout|Checkout is taking too long|Your answers were not accepted|Application fee amount can only be set for connected accounts|^Too many requests/i;

export function isExpectedToastText(text: string): boolean {
	if (text === "Application error") return true;
	return EXPECTED_TOAST_RE.test(text);
}

const eventExceptionText = (event: Sentry.ErrorEvent): string =>
	(event.exception?.values ?? [])
		.map((v) => `${v.type ?? ""} ${v.value ?? ""}`)
		.join(" ");

const isCrossOriginFrameAccessNoise = (
	event: Sentry.ErrorEvent,
	hint?: Sentry.EventHint,
): boolean => {
	const original = hint?.originalException as
		| { name?: string; message?: string }
		| undefined;
	const eventText = eventExceptionText(event);
	return (
		CROSS_ORIGIN_FRAME_RE.test(original?.message ?? "") ||
		CROSS_ORIGIN_FRAME_RE.test(eventText)
	);
};

const isCrossOriginFrameConsoleNoise = (args: unknown[]): boolean =>
	args.some((arg) => {
		if (typeof arg === "string") return CROSS_ORIGIN_FRAME_RE.test(arg);
		const message = (arg as { message?: unknown })?.message;
		return typeof message === "string" && CROSS_ORIGIN_FRAME_RE.test(message);
	});

const isHandledAxiosClientError = (
	event: Sentry.ErrorEvent,
	hint?: Sentry.EventHint,
): boolean => {
	if (isHandledClientError(hint?.originalException)) return true;
	return (event.exception?.values ?? []).some(
		(v) => v.type === "AxiosError" && /status code 4\d\d/.test(v.value ?? ""),
	);
};

/**
 * Axios never got an HTTP response (offline, abort, timeout). Buyers already
 * get a calm toast / retry; paging FRONTEND-5 as an unhandled crash is noise.
 * Partner 4xx/5xx keep `response` and stay visible (POC-423).
 */
const isAxiosNetworkNoise = (
	event: Sentry.ErrorEvent,
	hint?: Sentry.EventHint,
): boolean => {
	if (isAxiosNetworkError(hint?.originalException)) return true;
	return (event.exception?.values ?? []).some((v) =>
		isAxiosNetworkEventText(v.type, v.value),
	);
};

/**
 * React Query / axios abort in-flight requests when a component unmounts, a
 * query is superseded, or the tab navigates away. Those rejections surface as
 * `CanceledError: canceled` and are expected — not app bugs. Drop them so
 * payouts refetches don't page on benign navigation.
 */
const isAxiosCancelNoise = (
	event: Sentry.ErrorEvent,
	hint?: Sentry.EventHint,
): boolean => {
	const original = hint?.originalException as
		| { name?: string; code?: string; message?: string }
		| undefined;
	if (
		original?.name === "CanceledError" ||
		original?.code === "ERR_CANCELED" ||
		original?.message === "canceled"
	) {
		return true;
	}
	return (event.exception?.values ?? []).some(
		(v) =>
			v.type === "CanceledError" ||
			(v.type === "AxiosError" && v.value === "canceled"),
	);
};

/**
 * Browsers mask cross-origin script failures as the generic "Script error."
 * with no stack (synthetic mechanism). Common on /profile/payments and
 * /payouts when the payment processor's embed throws inside a cross-origin
 * iframe. Unfixable from our bundle — filter so real first-party errors stay
 * visible.
 */
const isCrossOriginScriptNoise = (event: Sentry.ErrorEvent): boolean =>
	(event.exception?.values ?? []).some((v) => {
		const value = v.value ?? "";
		const isScriptError = value === "Script error." || value === "Script error";
		if (!isScriptError) return false;
		const mechanism = v.mechanism;
		if (mechanism?.synthetic || mechanism?.type === "generic") return true;
		return (v.stacktrace?.frames ?? []).length === 0;
	});

const isAnalyticsTransportNoise = (
	event: Sentry.ErrorEvent,
	hint?: Sentry.EventHint,
): boolean => {
	const original = hint?.originalException as
		| { name?: string; message?: string }
		| undefined;
	const originalText = `${original?.name ?? ""} ${original?.message ?? ""}`;
	const eventText = (event.exception?.values ?? [])
		.map((v) => `${v.type ?? ""} ${v.value ?? ""}`)
		.join(" ");
	const text = `${originalText} ${eventText}`;
	return (
		/(posthog|mixpanel)/i.test(text) &&
		/AbortError|timed out|timeout|aborted|rate limit/i.test(text)
	);
};

const isExtensionOrInAppBrowserText = (text: string): boolean =>
	/(chrome|moz|safari)-extension:\/\//.test(text) ||
	/iabjs:\/\/|navigation_performance_logger/.test(text) ||
	/Java object is gone|window\.webkit\.messageHandlers/.test(text);

/**
 * Wallet extensions race to define `window.ethereum`; when two collide the
 * browser throws "Cannot redefine property: ethereum" from extension content
 * scripts, not our bundle (POC-861). Unfixable from checkout.
 */
const WALLET_INJECTION_RE =
	/Cannot redefine property: ethereum|can't redefine non-configurable property ["']?ethereum/i;

const isWalletInjectionNoise = (text: string): boolean =>
	WALLET_INJECTION_RE.test(text);

/**
 * `NotReadableError: The I/O read operation failed.` comes from the browser
 * or an in-app webview trying to read a picked file (screenshot tools, file
 * inputs). Checkout never reads user files, so this is host noise (POC-798).
 */
const isFileReadNoise = (text: string): boolean =>
	/NotReadableError|The I\/O read operation failed/i.test(text);

/**
 * react-router / history throws this when a navigation blocker is proceeded
 * after it was already unblocked — a benign race on rapid navigation
 * (`/posts/new/simple`). Navigation still completes; it is library-internal,
 * not an app bug (POC-464).
 */
const ROUTER_BLOCKER_RE = /Invalid blocker state transition/i;

const isRouterBlockerNoise = (text: string): boolean =>
	ROUTER_BLOCKER_RE.test(text);

/**
 * React logs deprecation/StrictMode warnings through `console.error`, so the
 * console bridge forwarded them as errors. They are warnings, not failures,
 * and they surface from headless preview/QA runs (POC-815: "Using
 * UNSAFE_componentWillMount ... SideEffect(...)"). Drop them.
 */
const REACT_DEV_WARNING_RE =
	/Using UNSAFE_componentWillMount|componentWillReceiveProps has been renamed|componentWillMount is deprecated|is deprecated in strict mode|Please update the following components/i;

const isReactDevWarningText = (text: string): boolean =>
	REACT_DEV_WARNING_RE.test(text);

/**
 * A stack overflow with no usable stack frame (only `filename: undefined`)
 * surfaced through the browser's global onerror. Nothing in our bundle to
 * point at — unactionable third-party/host recursion (POC-803).
 */
const isUnstackedStackOverflow = (event: Sentry.ErrorEvent): boolean =>
	(event.exception?.values ?? []).some((v) => {
		if (!/Maximum call stack/i.test(v.value ?? "")) return false;
		const frames = v.stacktrace?.frames ?? [];
		return frames.every((f) => !f.filename);
	});

const eventFrames = (event: Sentry.ErrorEvent) =>
	(event.exception?.values ?? []).flatMap((v) => v.stacktrace?.frames ?? []);

/** A frame that comes from a script file (our bundle or a vendor SDK). */
const isScriptFileFrame = (filename: string | undefined): boolean =>
	/\.(?:m?js|jsx|tsx?)(?:[?#:]|$)/.test(filename ?? "");

/**
 * Chrome on iOS and the Google app inject their own Closure-compiled scripts
 * into every page they show. When one of those rejects, Sentry records
 * `Error: La` (a minified identifier) with frames pointing at the HTML
 * document itself (`/checkout/success:195:338 (Vi)`) and no script file at
 * all — FRONTEND-7G/94/81, only ever on Chrome Mobile iOS / Google. Nothing in
 * our bundle is on the stack, so there is nothing to fix.
 */
const isInjectedWebviewScriptNoise = (event: Sentry.ErrorEvent): boolean => {
	const values = event.exception?.values ?? [];
	if (values.length === 0) return false;
	if (!values.every((v) => v.value === "La")) return false;
	return !eventFrames(event).some((f) => isScriptFileFrame(f.filename));
};

/**
 * Unhandled rejections whose reason carries no information at all — `undefined`
 * or `{}` — and no stack frame (FRONTEND-92/88). Our own code rejects with
 * Error instances (axios, fetch, explicit throws), so a reasonless, stackless
 * rejection comes from an injected or third-party script and cannot be traced
 * to anything we could fix. A rejection with ANY content or frame still ships.
 */
const OPAQUE_REJECTION_RE =
	/^(?:Non-Error promise rejection captured with value: (?:undefined|null)|Object captured as promise rejection with keys: \[object has no keys\])$/;

const isOpaqueRejectionNoise = (event: Sentry.ErrorEvent): boolean => {
	const values = event.exception?.values ?? [];
	if (values.length === 0) return false;
	if (!values.every((v) => OPAQUE_REJECTION_RE.test(v.value ?? ""))) {
		return false;
	}
	return eventFrames(event).length === 0;
};

/**
 * A wallet extension's provider rejecting its own request (EIP-1193 error
 * codes: 4001 rejected, 4100 unauthorized, 4200 unsupported, 4900 / 4901
 * disconnected) — FRONTEND-9T/9V, "The provider is disconnected from all
 * chains." on a checkout. The SDK records the rejected object under
 * `extra.__serialized__`; it never has a frame of ours, and checkout never
 * talks to a wallet provider. Dropped only when it is stackless and the
 * object is a provider error or was thrown from an extension.
 */
const EIP1193_PROVIDER_CODES = new Set([4001, 4100, 4200, 4900, 4901]);

const isWalletProviderRejection = (event: Sentry.ErrorEvent): boolean => {
	const values = event.exception?.values ?? [];
	if (values.length === 0) return false;
	if (
		!values.every((v) =>
			/^Object captured as promise rejection with keys: code, message/.test(
				v.value ?? "",
			),
		)
	) {
		return false;
	}
	if (eventFrames(event).length > 0) return false;
	const reason = event.extra?.__serialized__ as
		| { code?: unknown; stack?: unknown }
		| undefined;
	if (!reason) return false;
	return (
		(typeof reason.code === "number" &&
			EIP1193_PROVIDER_CODES.has(reason.code)) ||
		(typeof reason.stack === "string" &&
			isExtensionOrInAppBrowserText(reason.stack))
	);
};

/**
 * The payments partner's hosted Elements SDK script, on either its release
 * path (`/release/elements.js`) or its channel path (`/elements/amber/
 * elements.js`).
 */
const PARTNER_ELEMENTS_SCRIPT_RE =
	/\/(?:release|elements\/[\w-]+)\/elements\.js(?:[?#:]|$)/;

/**
 * The payments partner's hosted embedded-components SDK (`…/release/
 * elements.js`) mounts each payouts element inside its own promise chain and
 * re-raises a failed iframe mount as `Error: Failed to mount element`
 * (FRONTEND-3, /payouts). The React wrapper exposes no handler for that
 * promise, and the element keeps its own fallback on screen. Only dropped when
 * every frame is the partner's hosted script — the same message from our
 * bundle would still ship.
 */
const isPartnerEmbedMountNoise = (event: Sentry.ErrorEvent): boolean => {
	const values = event.exception?.values ?? [];
	if (!values.some((v) => v.value === "Failed to mount element")) return false;
	const frames = eventFrames(event);
	return (
		frames.length > 0 &&
		frames.every((f) => PARTNER_ELEMENTS_SCRIPT_RE.test(f.filename ?? ""))
	);
};

/**
 * Firefox raises its internal XPCOM failures (`NS_ERROR_UNEXPECTED: No error
 * message`, `NS_ERROR_FAILURE`, …) from inside the payments partner's hosted
 * Elements SDK (`cdn…/elements/amber/elements.js`) when its window `message`
 * listener touches a frame or storage that Firefox is tearing down
 * (FRONTEND-A5, /checkout, Firefox 146). The only other frame is Sentry's own
 * listener wrapper. Dropped only when every non-Sentry frame is the partner's
 * hosted script — the same error from our bundle still ships.
 */
const SENTRY_WRAPPER_FRAME_RE = /node_modules\/@sentry\//;

const isPartnerEmbedFirefoxInternalError = (
	event: Sentry.ErrorEvent,
): boolean => {
	const values = event.exception?.values ?? [];
	if (values.length === 0) return false;
	if (
		!values.every(
			(v) =>
				/^NS_ERROR_/.test(v.type ?? "") || /^NS_ERROR_/.test(v.value ?? ""),
		)
	) {
		return false;
	}
	const frames = eventFrames(event).filter(
		(f) => !SENTRY_WRAPPER_FRAME_RE.test(f.filename ?? f.abs_path ?? ""),
	);
	return (
		frames.length > 0 &&
		frames.every((f) =>
			PARTNER_ELEMENTS_SCRIPT_RE.test(f.abs_path ?? f.filename ?? ""),
		)
	);
};

/**
 * An UNHANDLED crash whose stack has frames, none of them from a script file:
 * only `<anonymous>` eval frames, inline frames on the HTML document itself,
 * or `[native code]`. That is code injected into the page by adware, an
 * extension or an in-app browser — FRONTEND-8Y ("Failed to fetch (selnor.fun)"
 * from `Object.postUserData` at `/logout:189`, past the end of our 185-line
 * index.html) and FRONTEND-82 (`JSON.stringify` cyclic-structure error from
 * "global code" at document line 1). Our bundle always runs from `/assets/*.js`
 * (or `.ts(x)` in dev), so ANY script-file frame keeps the event, and an event
 * with no stack at all is never dropped here.
 */
const isForeignScriptOnlyCrash = (event: Sentry.ErrorEvent): boolean => {
	const values = event.exception?.values ?? [];
	if (!values.some((v) => v.mechanism?.handled === false)) return false;
	const frames = eventFrames(event);
	if (frames.length === 0) return false;
	return frames.every(
		(f) =>
			!isScriptFileFrame(f.filename) &&
			!isScriptFileFrame(f.abs_path) &&
			!isScriptFileFrame(f.module),
	);
};

/**
 * An UNHANDLED fetch network failure against our own image CDN
 * (FRONTEND-9H: `TypeError: Load failed (d372ebr33sbljd.cloudfront.net)` on
 * /checkout, Opera Touch on iOS, the only frame Sentry's own listener
 * wrapper). The app renders CDN images with `<img>` and never `fetch()`es the
 * CDN without a catch (the product editor's thumbnail/file fetches both
 * catch), so the rejecting request came from the browser's injected script
 * (favicon / link-preview fetches) — nothing in the bundle to fix, and a
 * blocked image does not stop the page. Another host's failure still ships.
 */
const FOREIGN_CDN_FETCH_RE =
	/^(?:Load failed|Failed to fetch|NetworkError when attempting to fetch resource\.?) \(d372ebr33sbljd\.cloudfront\.net\)$/;

const isForeignCdnFetchRejection = (event: Sentry.ErrorEvent): boolean => {
	const values = event.exception?.values ?? [];
	if (values.length === 0) return false;
	return values.every(
		(v) =>
			v.mechanism?.handled === false &&
			FOREIGN_CDN_FETCH_RE.test(v.value ?? ""),
	);
};

/**
 * Stable grouping label for console-error captures. Ids and timings vary per
 * buyer, so a raw label split one failure into dozens of issues (the
 * "(after 110ms, …)" / "ch_…" family, FRONTEND-7J…95, 3T).
 */
export const normalizeFingerprintLabel = (label: string): string =>
	label
		.replace(/\b[a-f\d]{24}\b/gi, ":id")
		.replace(
			/\b(?:ch|plan|pay|prod|biz|pi|cs|sub|mem|rcpt|price)_[A-Za-z0-9]+/g,
			":id",
		)
		.replace(/\b\d+(?:\.\d+)?ms\b/g, ":ms")
		.slice(0, 120);

/**
 * Group `toast.promise` failures by endpoint and status. Without this every
 * dashboard 500 shared axios' internal stack and collapsed into ONE issue
 * (FRONTEND-1: /subscriptions/update, /unsubscribe, /profile/billing, …),
 * hiding which route actually broke.
 */
const axiosFingerprint = (error: unknown): string[] | undefined => {
	const axiosLike = error as {
		config?: { url?: unknown; method?: unknown };
		response?: { status?: unknown };
	};
	const url = axiosLike?.config?.url;
	if (typeof url !== "string" || !url) return undefined;
	const path = normalizeFingerprintLabel(url.split("?")[0] ?? url);
	const method =
		typeof axiosLike.config?.method === "string"
			? axiosLike.config.method.toUpperCase()
			: "GET";
	const status = axiosLike.response?.status;
	return [
		"toast.promise",
		method,
		path,
		typeof status === "number" ? String(status) : "no-response",
	];
};

/**
 * Deploy/host noise that is never an app bug we can fix from the bundle:
 * stale-chunk loads, sandboxed-storage denials, injected/embedded SDK chatter,
 * and browser/OS-level aborts. All pre-existing filters are kept above; this
 * is the shared bucket for the rest of the unresolved backlog.
 */
const HOST_NOISE_RE = new RegExp(
	[
		"Unable to preload CSS",
		"Failed to fetch dynamically imported module",
		"Importing a module script failed",
		"Lazy module resolved without a default export",
		"Cannot read properties of undefined \\(reading 'default'\\)",
		"Failed to read the '(localStorage|sessionStorage)' property",
		"different security origin than its top-level frame",
		"start an Apple Pay session",
		"Apple Pay session",
		"WhopElementError",
		"\\[React Scan\\]",
		"Failed to establish WebSocket connection",
		"Applying a mismatched transaction",
		"signal is aborted without reason",
		// Outlook SafeLinks / Bing link-preview crawlers (Azure IPs) executing
		// checkout links reject a non-Error from their own RPC bridge
		// (POCKETSFLOW-FRONTEND-1K) — not a buyer, not our code.
		"Object Not Found Matching Id:\\d+, MethodName:",
		// DuckDuckGo's browser runtime messaging its own (closed) tab
		// (FRONTEND-2E) — browser/extension plumbing, not page code.
		"Invalid call to runtime\\.sendMessage\\(\\)\\. Tab not found",
		// Sender (NEAR) wallet extension logging its own provider state
		// through the page console (FRONTEND-93).
		"Sender: Failed to get initial state",
		// The payments partner's hosted Elements SDK logs its own boot
		// diagnostics through console.error under its "[… elements]" prefix
		// ("controller .state() seed failed — its elements fail to boot:
		// Failed to fetch", "coordinator iframe failed to boot",
		// FRONTEND-7F/7N). The element reports the same failure to checkout's
		// onError, which falls back to the legacy embed before ready — the
		// SDK's own log line is redundant.
		"\\[whop elements\\] .*fail(?:s|ed)? to boot",
	].join("|"),
	"i",
);

const isHostNoiseText = (text: string): boolean => HOST_NOISE_RE.test(text);

const isAnalyticsTransportText = (text: string): boolean =>
	/(posthog|mixpanel)/i.test(text) &&
	/AbortError|timed out|timeout|aborted|rate limit/i.test(text);

const shouldDropException = (error: unknown): boolean => {
	if (isHandledClientError(error)) return true;
	const url = (error as { config?: { url?: string } })?.config?.url;
	if (typeof url === "string" && url.includes("fetch-file")) return true;
	const message = error instanceof Error ? error.message : String(error ?? "");
	const name = error instanceof Error ? error.name : "";
	const text = `${name} ${message}`;
	if (message === "Script error." || message === "Script error") return true;
	if (CROSS_ORIGIN_FRAME_RE.test(message)) return true;
	if (isExtensionOrInAppBrowserText(text)) return true;
	if (isWalletInjectionNoise(text)) return true;
	if (isFileReadNoise(text)) return true;
	if (isRouterBlockerNoise(text)) return true;
	if (isReactDevWarningText(text)) return true;
	if (isHostNoiseText(text)) return true;
	if (isAnalyticsTransportText(text)) return true;
	if (
		name === "CanceledError" ||
		(error as { code?: string })?.code === "ERR_CANCELED" ||
		message === "canceled"
	) {
		return true;
	}
	if (isAxiosNetworkError(error)) return true;
	if (name === "AxiosError" && /status code 4\d\d/.test(message)) return true;
	return false;
};

const beforeSend = (
	event: Sentry.ErrorEvent,
	hint?: Sentry.EventHint,
): Sentry.ErrorEvent | null => {
	if (isExtensionOrInAppBrowserNoise(event)) return null;
	const noiseText = eventExceptionText(event);
	if (isWalletInjectionNoise(noiseText)) return null;
	if (isFileReadNoise(noiseText)) return null;
	if (isRouterBlockerNoise(noiseText)) return null;
	if (isReactDevWarningText(noiseText)) return null;
	if (isHostNoiseText(noiseText)) return null;
	if (isUnstackedStackOverflow(event)) return null;
	if (isInjectedWebviewScriptNoise(event)) return null;
	if (isOpaqueRejectionNoise(event)) return null;
	if (isWalletProviderRejection(event)) return null;
	if (isPartnerEmbedMountNoise(event)) return null;
	if (isPartnerEmbedFirefoxInternalError(event)) return null;
	if (isForeignScriptOnlyCrash(event)) return null;
	if (isForeignCdnFetchRejection(event)) return null;
	if (isCrossOriginFrameAccessNoise(event, hint)) return null;
	if (isHandledAxiosClientError(event, hint)) return null;
	if (isAxiosCancelNoise(event, hint)) return null;
	if (isAxiosNetworkNoise(event, hint)) return null;
	if (isCrossOriginScriptNoise(event)) return null;
	if (isAnalyticsTransportNoise(event, hint)) return null;
	const axiosUrl = (hint?.originalException as { config?: { url?: string } })
		?.config?.url;
	if (typeof axiosUrl === "string" && axiosUrl.includes("fetch-file")) {
		return null;
	}
	return event;
};

export const initSentry = (): void => {
	if (initialised || !enabled || !DSN) return;
	// Mark initialised synchronously so error reporters queue against the
	// loader instead of dropping events while the SDK chunk is in flight.
	initialised = true;
	void loadSentry()
		.then((Sentry) => {
			Sentry.init({
				dsn: DSN,
				environment: ENV,
				release: packageJson.version,
				sendDefaultPii: true,
				attachStacktrace: true,
				enableLogs: true,
				integrations: [Sentry.browserTracingIntegration()],
				replaysSessionSampleRate: REPLAY_SESSION_SAMPLE_RATE,
				replaysOnErrorSampleRate: REPLAY_ERROR_SAMPLE_RATE,
				tracesSampleRate: isProd ? 0.1 : 1.0,
				tracePropagationTargets: ["localhost", /^https:\/\/api\./],
				beforeSend,
			});

			Sentry.setTag("surface", isSalesSurface() ? "sales" : "dashboard");
			instrumentToastErrors(Sentry);
			startSessionReplay();
		})
		.catch(() => {
			// Analytics must never break the app shell.
		});
};

export const scheduleSalesSentry = (): void => {
	if (scheduled) return;
	scheduled = true;
	const start = () => initSentry();
	if (typeof window.requestIdleCallback === "function") {
		window.requestIdleCallback(start, { timeout: 4000 });
	} else {
		window.setTimeout(start, 1500);
	}
};

export const reportException = (
	error: unknown,
	extra?: Record<string, unknown>,
): void => {
	if (!initialised) return;
	if (shouldDropException(error)) return;
	void loadSentry()
		.then((Sentry) => {
			Sentry.captureException(error, extra ? { extra } : undefined);
		})
		.catch(() => {});
};

const consoleArgsToText = (args: unknown[]): string =>
	args
		.map((a) => {
			if (typeof a === "string") return a;
			if (a instanceof Error) return a.message;
			try {
				return typeof a === "object" ? JSON.stringify(a) : String(a);
			} catch {
				return String(a);
			}
		})
		.join(" ") || "console.error";

export const reportConsoleError = (args: unknown[]): void => {
	if (!initialised) return;
	if (args.some(isHandledClientError)) return;
	if (args.some(isHandledClientErrorDetails)) return;
	if (isValidationConsoleNoise(args)) return;
	if (isFetchFileConsoleNoise(args)) return;
	if (isCrossOriginFrameConsoleNoise(args)) return;

	// FRONTEND-6R (#1815): run the text guards against the COMBINED args, not
	// only the Error's own name/message. The PostHog SDK logs
	// "[PostHog.js] capture call ignored due to client rate limiting" as a
	// trailing string beside a generic Error, which previously slipped through
	// the firstError branch below and paged as real console-error noise.
	const message = consoleArgsToText(args);
	if (isExtensionOrInAppBrowserText(message)) return;
	if (isAnalyticsTransportText(message)) return;
	if (isReactDevWarningText(message)) return;
	if (isHostNoiseText(message)) return;

	const firstError = args.find((a): a is Error => a instanceof Error);
	if (firstError) {
		if (shouldDropException(firstError)) return;
		void loadSentry()
			.then((Sentry) => {
				Sentry.captureException(firstError, {
					tags: { source: "console.error" },
					extra: { arguments: args.map((a) => toMessage(a)) },
				});
			})
			.catch(() => {});
		return;
	}
	const staticLabel =
		(args.find((a) => typeof a === "string") as string | undefined) ?? message;
	void loadSentry()
		.then((Sentry) => {
			Sentry.captureMessage(message, {
				level: "error",
				tags: { source: "console.error" },
				fingerprint: ["console-error", normalizeFingerprintLabel(staticLabel)],
			});
		})
		.catch(() => {});
};

const instrumentToastErrors = (Sentry: SentryModule): void => {
	const originalError = toast.error.bind(toast);
	toast.error = ((message: Parameters<typeof originalError>[0], opts) => {
		const text = toMessage(message);
		const skipSentry =
			(opts as { sentry?: boolean } | undefined)?.sentry === false ||
			isExpectedToastText(text) ||
			text === NETWORK_ERROR_TOAST ||
			text === networkErrorToast();
		if (!skipSentry) {
			Sentry.captureMessage(text, {
				level: "warning",
				tags: { source: "toast.error" },
				fingerprint: ["toast.error", text.slice(0, 120)],
			});
		}
		return originalError(message, opts);
	}) as typeof toast.error;

	const originalPromise = toast.promise.bind(toast);
	toast.promise = ((promise, msgs, opts) => {
		Promise.resolve(promise).catch((err: unknown) => {
			if (isHandledClientError(err)) return;
			if (shouldDropException(err)) return;
			const error =
				err instanceof Error ? err : new Error(toMessage(msgs?.error));
			const fingerprint = axiosFingerprint(err);
			Sentry.captureException(error, {
				tags: { source: "toast.promise" },
				extra: { toastMessage: toMessage(msgs?.error) },
				...(fingerprint ? { fingerprint } : {}),
			});
		});
		// The rejection above is the ONE report for this promise (or a
		// deliberate drop, e.g. a 4xx business rejection). react-hot-toast then
		// shows the error through the wrapped toast.error, which used to capture
		// the same failure a second time as a message — and page expected 4xx
		// reasons such as a refund the payouts provider declined (FRONTEND-6S).
		const errorOpts = (opts as { error?: Record<string, unknown> } | undefined)
			?.error;
		const promiseOpts = {
			...opts,
			error: { ...errorOpts, sentry: false },
		} as typeof opts;
		return originalPromise(promise, msgs, promiseOpts);
	}) as typeof toast.promise;
};

export const getSentryEnabled = (): boolean => enabled;
export const isSentryInitialized = (): boolean => initialised;

export const __resetSentryForTests = (): void => {
	initialised = false;
	scheduled = false;
	sentryModulePromise = null;
};

export const __sentryTestHooks = {
	shouldDropException,
	isValidationConsoleNoise,
	isFetchFileConsoleNoise,
	isCrossOriginFrameConsoleNoise,
	isExpectedToastText,
	beforeSend,
	isAxiosCancelNoise,
	isAxiosNetworkNoise,
	isCrossOriginScriptNoise,
	isInjectedWebviewScriptNoise,
	isOpaqueRejectionNoise,
	isPartnerEmbedMountNoise,
	isPartnerEmbedFirefoxInternalError,
	isForeignScriptOnlyCrash,
	isForeignCdnFetchRejection,
	axiosFingerprint,
};
